import { LabelId, UserCommunication, ViewType } from '@this+that/models';

import { VIEW_TYPES } from '../../../enums/viewTypes';

export enum UserCommunicationFilter {
  INBOX = 'inbox',
  SENT = 'sent',
  DRAFT = 'draft',
  SPAM = 'spam',
  TRASH = 'trash',
  ARCHIVED = 'archived',
  PROMOTIONS = 'promotions',
  FORUMS = 'forums',
  SOCIAL = 'social',
  UPDATES = 'updates',
  ALL = 'all',
  CHANNELS = 'channels',
  DIRECT = 'direct',
  LABELS = 'labels',
  SEARCH = 'search',
  CHATS = 'chats',
}

export const FILTER_VIEW_TYPE: Partial<
  Record<UserCommunicationFilter, ViewType>
> = {
  [UserCommunicationFilter.INBOX]: VIEW_TYPES.PRIMARY,
  [UserCommunicationFilter.SENT]: VIEW_TYPES.SENT,
  [UserCommunicationFilter.DRAFT]: VIEW_TYPES.DRAFT,
  [UserCommunicationFilter.SPAM]: VIEW_TYPES.SPAM,
  [UserCommunicationFilter.TRASH]: VIEW_TYPES.TRASH,
  [UserCommunicationFilter.PROMOTIONS]: VIEW_TYPES.PROMOTIONS,
  [UserCommunicationFilter.SOCIAL]: VIEW_TYPES.SOCIAL,
  [UserCommunicationFilter.UPDATES]: VIEW_TYPES.UPDATES,
  [UserCommunicationFilter.FORUMS]: VIEW_TYPES.FORUMS,
  [UserCommunicationFilter.ALL]: VIEW_TYPES.ALL_EMAILS,
};

const hasLabel = (arr?: string[]) =>
  arr?.some((item) => item.startsWith('Label_'));

export const isUserCommunicationInInbox = (
  userCommunication: UserCommunication,
): boolean =>
  Boolean(
    userCommunication.labelIds?.includes(LabelId.INBOX) &&
      !userCommunication.labelIds?.includes(LabelId.TRASH),
  );

export const hasInboxLabel = (userCommunication: UserCommunication): boolean =>
  Boolean(userCommunication.labelIds?.includes(LabelId.INBOX));

export const useFilterUserCommunications = (
  userCommunications: UserCommunication[],
  filterBy: UserCommunicationFilter,
) => {
  const filteredCommunications = userCommunications
    ?.filter((userCommunication) => {
      switch (filterBy) {
        case UserCommunicationFilter.SENT:
          return (
            userCommunication.labelIds?.includes(LabelId.SENT) &&
            !userCommunication.labelIds?.includes(LabelId.TRASH)
          );
        case UserCommunicationFilter.DRAFT:
          return (
            userCommunication.labelIds?.includes(LabelId.DRAFT) &&
            !userCommunication.labelIds?.includes(LabelId.TRASH)
          );
        case UserCommunicationFilter.SPAM:
          return (
            userCommunication.labelIds?.includes(LabelId.SPAM) &&
            !userCommunication.labelIds?.includes(LabelId.TRASH)
          );
        case UserCommunicationFilter.TRASH:
          return userCommunication.labelIds?.includes(LabelId.TRASH);
        case UserCommunicationFilter.INBOX:
          return isUserCommunicationInInbox(userCommunication);
        case UserCommunicationFilter.ARCHIVED:
          return (
            !userCommunication.labelIds?.includes(LabelId.INBOX) &&
            !userCommunication.labelIds?.includes(LabelId.TRASH)
          );
        case UserCommunicationFilter.PROMOTIONS:
          return (
            userCommunication.labelIds?.includes(LabelId.CATEGORY_PROMOTIONS) &&
            !userCommunication.labelIds?.includes(LabelId.TRASH)
          );
        case UserCommunicationFilter.FORUMS:
          return (
            userCommunication.labelIds?.includes(LabelId.CATEGORY_FORUMS) &&
            !userCommunication.labelIds?.includes(LabelId.TRASH)
          );
        case UserCommunicationFilter.SOCIAL:
          return (
            userCommunication.labelIds?.includes(LabelId.CATEGORY_SOCIAL) &&
            !userCommunication.labelIds?.includes(LabelId.TRASH)
          );
        case UserCommunicationFilter.UPDATES:
          return (
            userCommunication.labelIds?.includes(LabelId.CATEGORY_UPDATES) &&
            !userCommunication.labelIds?.includes(LabelId.TRASH)
          );
        case UserCommunicationFilter.LABELS:
          return (
            hasLabel(userCommunication.labelIds) &&
            !userCommunication.labelIds?.includes(LabelId.TRASH)
          );
        case UserCommunicationFilter.ALL:
          return true;
        default:
          return true;
      }
    })
    .map((userCommunication) => {
      if (filterBy === UserCommunicationFilter.SENT) {
        return {
          ...userCommunication,
          author: `To: ${
            userCommunication.author || userCommunication.authorEmail
          }`,
        };
      }
      return userCommunication;
    });

  return { filteredCommunications };
};
